(() => {
  'use strict';

  if (window.BaringNavigationInitialized) return;
  window.BaringNavigationInitialized = true;

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const triggerSelector = name => `[data-baring-${name}-open]`;

  class DialogController {
    constructor(dialog) {
      this.dialog = dialog;
      this.name = dialog.dataset.baringDialog;
      this.opener = null;

      document.querySelectorAll(triggerSelector(this.name)).forEach(trigger => {
        trigger.setAttribute('aria-controls', dialog.id);
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('click', event => {
          event.preventDefault();
          this.open(trigger);
        });
      });

      dialog.querySelectorAll('[data-baring-dialog-close]').forEach(button => {
        button.addEventListener('click', () => this.close());
      });

      dialog.addEventListener('keydown', event => this.onKeydown(event));
      dialog.addEventListener('close', () => this.onClose());
    }

    getFocusableElements() {
      return Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(element => {
        return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.offsetParent !== null;
      });
    }

    open(trigger) {
      if (this.dialog.open) return;

      document.querySelectorAll('dialog[data-baring-dialog][open]').forEach(openDialog => {
        if (openDialog !== this.dialog) openDialog.close();
      });

      this.opener = trigger;
      this.dialog.showModal();
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('baring-dialog-open');

      requestAnimationFrame(() => {
        const target = this.dialog.querySelector('[data-baring-search-input]') || this.getFocusableElements()[0];
        if (target) target.focus({ preventScroll: true });
        this.dialog.dispatchEvent(new CustomEvent('baring:dialog-open'));
      });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onClose() {
      document.querySelectorAll(triggerSelector(this.name)).forEach(trigger => {
        trigger.setAttribute('aria-expanded', 'false');
      });

      if (!document.querySelector('dialog[data-baring-dialog][open]')) {
        document.body.classList.remove('baring-dialog-open');
      }

      if (this.opener && this.opener.isConnected) {
        this.opener.focus({ preventScroll: true });
      }

      this.dialog.dispatchEvent(new CustomEvent('baring:dialog-close'));
      this.opener = null;
    }
  }

  class PredictiveSearch {
    constructor(dialog) {
      this.dialog = dialog;
      this.form = dialog.querySelector('[data-baring-search-form]');
      this.input = dialog.querySelector('[data-baring-search-input]');
      this.results = dialog.querySelector('[data-baring-search-results]');
      this.status = dialog.querySelector('[data-baring-search-status]');
      this.endpoint = dialog.dataset.suggestUrl;
      this.messages = {
        hint: dialog.dataset.hintMessage,
        loading: dialog.dataset.loadingMessage,
        unavailable: dialog.dataset.errorMessage,
        empty: dialog.dataset.emptyMessage
      };
      this.cache = new Map();
      this.abortController = null;
      this.timer = null;
      this.sequence = 0;
      this.activeIndex = -1;
      this.options = [];

      if (!this.form || !this.input || !this.results || !this.status || !this.endpoint) return;

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', event => this.onKeydown(event));
      this.input.addEventListener('search', () => this.onInput());
      this.results.addEventListener('pointermove', event => this.onPointerMove(event));
      this.dialog.addEventListener('baring:dialog-open', () => this.onOpen());
      this.dialog.addEventListener('baring:dialog-close', () => this.onClose());
    }

    onOpen() {
      const term = this.input.value.trim();
      if (term.length >= 2 && !this.results.childElementCount) this.queue(term, 0);
    }

    onClose() {
      window.clearTimeout(this.timer);
      if (this.abortController) this.abortController.abort();
      this.clearActive();
    }

    onInput() {
      const term = this.input.value.trim();
      window.clearTimeout(this.timer);

      if (term.length < 2) {
        if (this.abortController) this.abortController.abort();
        this.sequence += 1;
        this.results.replaceChildren();
        this.results.setAttribute('aria-busy', 'false');
        this.results.removeAttribute('role');
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
        this.status.textContent = this.messages.hint;
        this.clearActive();
        return;
      }

      this.queue(term, 160);
    }

    queue(term, delay) {
      this.timer = window.setTimeout(() => this.load(term), delay);
    }

    async load(term) {
      const normalizedTerm = term.toLocaleLowerCase();
      const requestNumber = ++this.sequence;

      if (this.cache.has(normalizedTerm)) {
        this.render(this.cache.get(normalizedTerm));
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = this.messages.loading;

      const url = new URL(this.endpoint, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'query,product,collection,page,article');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', 'predictive-search');

      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: 'text/html' },
          signal: this.abortController.signal
        });

        if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);

        const text = await response.text();
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const section = documentFragment.querySelector('#shopify-section-predictive-search');

        if (!section) throw new Error('Predictive search section was not returned');
        if (requestNumber !== this.sequence) return;

        const markup = section.innerHTML;
        this.cache.set(normalizedTerm, markup);
        this.render(markup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (requestNumber !== this.sequence) return;

        this.results.replaceChildren();
        this.results.setAttribute('aria-busy', 'false');
        this.results.removeAttribute('role');
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
        this.status.textContent = this.messages.unavailable;
        this.clearActive();
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.results.setAttribute('aria-busy', 'false');
      this.options = Array.from(this.results.querySelectorAll('[role="option"]'));
      this.activeIndex = -1;
      if (this.options.length) this.results.setAttribute('role', 'listbox');
      else this.results.removeAttribute('role');

      const root = this.results.querySelector('[data-baring-predictive-root]');
      const message = root && root.dataset.resultMessage;
      this.status.textContent = message || this.messages.empty;
      this.input.setAttribute('aria-expanded', String(this.options.length > 0));
      this.input.removeAttribute('aria-activedescendant');
    }

    onKeydown(event) {
      if (!this.options.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.setActive((this.activeIndex + 1) % this.options.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const nextIndex = this.activeIndex <= 0 ? this.options.length - 1 : this.activeIndex - 1;
        this.setActive(nextIndex);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        this.options[this.activeIndex].click();
      }
    }

    onPointerMove(event) {
      const option = event.target.closest('[role="option"]');
      if (!option || !this.results.contains(option)) return;
      const index = this.options.indexOf(option);
      if (index >= 0 && index !== this.activeIndex) this.setActive(index, false);
    }

    setActive(index, shouldScroll = true) {
      this.clearActive();
      const option = this.options[index];
      if (!option) return;

      this.activeIndex = index;
      option.classList.add('is-active');
      option.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', option.id);

      if (shouldScroll) option.scrollIntoView({ block: 'nearest' });
    }

    clearActive() {
      this.options.forEach(option => {
        option.classList.remove('is-active');
        option.setAttribute('aria-selected', 'false');
      });
      this.activeIndex = -1;
      if (this.input) this.input.removeAttribute('aria-activedescendant');
    }
  }

  const initialize = () => {
    document.querySelectorAll('dialog[data-baring-dialog]').forEach(dialog => {
      new DialogController(dialog);
    });

    const searchDialog = document.querySelector('dialog[data-baring-dialog="search"]');
    if (searchDialog) new PredictiveSearch(searchDialog);

    document.querySelectorAll('[data-baring-localization-select]').forEach(select => {
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (!form) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
